import {
  CustomFieldCategoricalMultiple,
  CustomFieldCategoricalSingle,
  CustomFieldDefinition,
  CustomFields,
  Location,
} from '@nx-smartmonkey/shared/domain';
import { parseSecondsToHM, validatePhoneNumber } from '@nx-smartmonkey/shared/helpers';
import { geocodingService } from '../../application/GeocodingService';

import { getLocationFromCoordinates } from '../../services/requests';
import { intl } from '../IntGlobalProvider';
import { isNumeric, normalizeString, toCoords, toSeconds } from './Common';

export const parseTimewindowToExcel = (value: [number, number]): string => {
  if (!value) {
    return ``;
  }
  return `${parseSecondsToHM(value[0])}-${parseSecondsToHM(value[1])}`;
};

export const parseTimewindowsToExcel = (value: [number, number][]) => {
  return value.map((tw) => parseTimewindowToExcel(tw)).join(`, `);
};

export const parseTW = (timeWindow: [number, number] | [number, number][]) => {
  if (Array.isArray(timeWindow)) {
    if (Array.isArray(timeWindow[0])) {
      return parseTimewindowsToExcel(timeWindow as [number, number][]);
    }

    return parseTimewindowToExcel(timeWindow as [number, number]);
  }

  return null;
};

export const parseCustomFieldValues = ({
  customFields,
  projectCustomFields,
}: {
  customFields?: CustomFields;
  projectCustomFields: CustomFieldDefinition[];
}) => {
  const values: any = [];

  projectCustomFields.forEach((field: CustomFieldDefinition) => {
    let value = null;

    if (
      Object.keys(customFields?.value ?? {})
        .map((key) => key)
        .includes(field.custom_id)
    ) {
      if (customFields!.value[field.custom_id] !== undefined && customFields!.value[field.custom_id] !== null) {
        switch (field.field_type) {
          case `text`:
          case `numerical`:
            value = customFields!.value[field.custom_id].toString();
            break;
          case `boolean`:
            value = intl.formatMessage({ id: customFields!.value[field.custom_id] === true ? `yes` : `no` });
            break;
          case `categorical`:
            value = field.multiple
              ? ((customFields!.value[field.custom_id] ?? []) as CustomFieldCategoricalMultiple)
                  .map((option: CustomFieldCategoricalSingle) => option.label)
                  .join(`, `)
              : (customFields!.value[field.custom_id] as CustomFieldCategoricalSingle).label;
            break;
          default:
            break;
        }
      }
    }
    values.push(value);
  });
  return values;
};

export const parseString = async (string: any) => {
  if (string === null) {
    return undefined;
  }
  return new Promise((resolve: any) => {
    try {
      resolve(`${string}`);
    } catch (e) {
      resolve(``);
    }
  });
};

export const parsePhone = async (phone: any) => {
  if (phone === null) {
    return undefined;
  }
  return new Promise((resolve: any) => {
    try {
      const newPhone = `+${Number.parseInt(`${phone}`.replace(/\D+/g, ``), 10)}`;
      const isValid = validatePhoneNumber(`${newPhone}`);
      if (isValid) {
        resolve(`${newPhone}`);
      } else {
        resolve(null);
      }
    } catch (e) {
      resolve(null);
    }
  });
};

export const parseBoolean = async (value: any) => {
  if (value === null) {
    return undefined;
  }
  return new Promise((resolve: any) => {
    try {
      // eslint-disable-next-line
      if (isNaN(value)) {
        resolve(null);
      }
      resolve(Boolean(value));
    } catch (e) {
      resolve(null);
    }
  });
};

const parseListSemicolon = async (list: any): Promise<string[] | undefined> => {
  if (list === null) {
    return undefined;
  }
  return new Promise((resolve: any) => {
    try {
      resolve(list.split(`;`).map((v: any) => v.trim()));
    } catch (e) {
      resolve(null);
    }
  });
};

export const parseNumber = async (number: any) => {
  if (number === null) {
    return undefined;
  }
  return new Promise((resolve: any) => {
    try {
      // eslint-disable-next-line
      if (isNaN(number)) {
        resolve(null);
      }
      resolve(parseFloat(number));
    } catch (e) {
      resolve(null);
    }
  });
};

export const parseInt = async (number: any) => {
  if (number === null) {
    return undefined;
  }
  return new Promise((resolve: any) => {
    try {
      // eslint-disable-next-line
      if (isNaN(number)) {
        resolve(null);
      }
      resolve(Math.ceil(parseFloat(number)));
    } catch (e) {
      resolve(null);
    }
  });
};

export const parseMinutes = async (number: any) => {
  if (number === null) {
    return undefined;
  }
  return new Promise((resolve: any) => {
    try {
      // eslint-disable-next-line
      if (isNaN(number)) {
        resolve(null);
      }
      resolve(Math.ceil(parseFloat(number) * 60));
    } catch (e) {
      resolve(null);
    }
  });
};

export const parseAddresses = async ({ addresses, projectId }: { addresses: string[]; projectId?: string }) => {
  // const cleanAddresses = addresses.map(address => cleanString(address));

  const addressesMap: { [key: number]: string } = {};
  const coordinatesParsed: { [coordinate: string]: [number, number] } = {};
  const coordinatesResult: {
    [coordinates: string]: Location | null;
  } = {};
  const directionsResult: {
    [directions: string]: Location | null;
  } = {};

  addresses.forEach((address, idx) => {
    addressesMap[idx] = address;

    try {
      // Check if address are coordinates
      const coordinates = toCoords(address);
      if (coordinates) {
        coordinatesParsed[address] = coordinates;
        coordinatesResult[address] = null;
      } else {
        directionsResult[address] = null;
      }
    } catch (_e) {
      // Do nothing
    }
  });

  try {
    const directionsResponse =
      Object.keys(directionsResult).length > 0
        ? await geocodingService.postGeocode({ addresses: Object.keys(directionsResult), projectId })
        : [];
    const coordinatesResponse =
      Object.values(coordinatesParsed).length > 0
        ? await getLocationFromCoordinates(
            Object.values(coordinatesParsed).map((val) => ({ lat: val[0], lng: val[1] }))
          )
        : [];

    Object.keys(directionsResult).forEach((id: string, idx: number) => {
      directionsResult[id] = directionsResponse[idx];
    });
    Object.keys(coordinatesResult).forEach((id, idx) => {
      coordinatesResponse[idx].original_input = Object.keys(coordinatesParsed)[idx];
      coordinatesResult[id] = coordinatesResponse[idx];
    });

    const response = [];
    for (const address of addresses) {
      response.push(directionsResult[address] || coordinatesResult[address] || null);
    }
    return response;
  } catch (err: any) {
    throw err?.response?.status === 400 ? err?.response?.data?.messageId : err;
  }
};

export const parseCategorical = async (rawValue: string, field: CustomFieldDefinition) => {
  if (rawValue === null) return undefined;

  const value = await parseString(rawValue);

  // Extract the values that are splitted using semicolons.
  let fieldDataValues = await parseListSemicolon(value);
  if (!fieldDataValues) {
    return fieldDataValues;
  }

  fieldDataValues = fieldDataValues.map((fieldValue: any) => normalizeString(fieldValue));

  // Value must be an array of strings.
  if (!Array.isArray(fieldDataValues) || !!fieldDataValues.find((val: any) => typeof val !== `string`)) return null;

  // Value must be an array of one item if field type is categorical single.
  if (!field.multiple && fieldDataValues.length > 1) return null;

  // Value have multiple options selected but field does not allow multiple selection.
  if (fieldDataValues.length > 1 && !field.multiple) return null;

  const fieldOptions = field.options?.map((option: string) => normalizeString(option));
  if (!fieldOptions) {
    return null;
  }

  if (fieldDataValues.find((val: string) => !fieldOptions.includes(normalizeString(val)))) return null;

  if (field.multiple) {
    const selectedOptions: { id: number; label: string }[] = [];
    fieldOptions.forEach((option: string, index: number) => {
      if ((fieldDataValues || []).includes(option)) {
        selectedOptions.push({
          id: index,
          label: option,
        });
      }
    });
    return selectedOptions;
  } else {
    const selectedOptionIndex = fieldOptions.indexOf(fieldDataValues[0]);
    return { id: selectedOptionIndex, label: fieldDataValues[0] };
  }
};

export const parseList = (list: any) => {
  if (list === null) {
    return undefined;
  }
  try {
    const string = (isNumeric(list) ? `${list}` : list).trim();
    return string
      .split(`,`)
      .map((v: any) => v.trim())
      .filter((v: any) => v !== ``);
  } catch (e) {
    return null;
  }
};

export const parseStringArray = (array?: string[]) => {
  return (array ?? []).reduce((p: any, n: any, i: any) => (i !== (array ?? []).length - 1 ? `${p + n}, ` : p + n), ``);
};

export const parseTimeWindows = (timeWindows: any) => {
  if (timeWindows === null) {
    return undefined;
  }
  try {
    const splitted = timeWindows.split(`;`).map((v: any) => v.trim());
    const arrayTW = splitted.map((val: any) => {
      if (val !== ``) {
        const sText = val.split(`-`)[0].trim();
        const eText = val.split(`-`)[1].trim();
        const timeStart = toSeconds(sText);
        let timeEnd = toSeconds(eText);
        if (timeStart === null || timeEnd === null) {
          return undefined;
        }
        // Adding 24 hours if timeStart is higher than timeEnd. Each time is from different days (maximum 1 day).
        timeEnd = timeStart > timeEnd ? timeEnd + 3600 * 24 : timeEnd;
        return [timeStart, timeEnd];
      }
      return null;
    });
    if (arrayTW.includes(null)) {
      return undefined;
    }
    // Check if there are overlaps
    const allTW: Array<[number, number]> = arrayTW
      .filter((tw: any) => tw)
      .sort((tw1: [number, number], tw2: [number, number]) => (tw1[0] < tw2[0] ? -1 : 1));

    for (let index = 0; index < allTW.length; index++) {
      const current = allTW[index];
      const next = allTW[index + 1];
      if (next) {
        if (current[1] >= next[0]) {
          throw new Error(`Time windows overlap`);
        }
      }
    }
    return allTW;
  } catch (e) {
    return null;
  }
};

export const parseTimeWindow = async (timeWindow: any) => {
  if (timeWindow === null) {
    return undefined;
  }
  try {
    const sText = timeWindow.split(`-`)[0].trim();
    const eText = timeWindow.split(`-`)[1].trim();
    const timeStart = toSeconds(sText);
    let timeEnd = toSeconds(eText);
    if (timeStart === null || timeEnd === null) {
      return undefined;
    }
    // Adding 24 hours if timeStart is higher than timeEnd. Each time is from different days (maximum 1 day).
    timeEnd = timeStart > timeEnd ? timeEnd + 3600 * 24 : timeEnd;
    return [timeStart, timeEnd];
  } catch (e) {
    return undefined;
  }
};
